/**
 * Internal team roster (T060, US6 — FR-010 / Art. I). ADMIN-ONLY: person-level data. Lists the
 * roster and lets an admin add/update a Person (used to classify message authors internal vs
 * external). Non-admins see a denied banner; the persons collection is admin-only at the rules level.
 */
import { useEffect, useState } from 'react';
import type { SessionUser } from '../services/auth';
import type { Person } from '../types/entities';
import { listPersons, upsertPerson } from '../services/roster';

export function Roster({ user }: { user: SessionUser }) {
  const isAdmin = user.role === 'admin';

  const [people, setPeople] = useState<Person[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [slackId, setSlackId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    if (!isAdmin) return;
    listPersons()
      .then(setPeople)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load roster'));
  };

  useEffect(refresh, [isAdmin]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await upsertPerson({
        display_name: displayName.trim(),
        workspace_email: email.trim(),
        source_ids: slackId.trim() ? { slack: slackId.trim(), email: email.trim() } : { email: email.trim() },
        is_internal: true,
      });
      setDisplayName('');
      setEmail('');
      setSlackId('');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add person failed');
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <section>
        <h1>Team roster</h1>
        <p style={banner}>Person-level data is admin-only (Art. I).</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Team roster ({people.length})</h1>

      <fieldset disabled={busy} style={fs}>
        <legend>Add a team member</legend>
        <label style={row}>
          <span style={lbl}>Display name</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label style={row}>
          <span style={lbl}>Workspace email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@cosmic-chimps.com" />
        </label>
        <label style={row}>
          <span style={lbl}>Slack id (optional)</span>
          <input value={slackId} onChange={(e) => setSlackId(e.target.value)} placeholder="U0123ABC" />
        </label>
        <button type="button" onClick={() => void add()} disabled={!displayName.trim() || !email.trim()}>
          {busy ? 'Saving…' : 'Add to roster'}
        </button>
      </fieldset>

      <ul style={{ marginTop: 12 }}>
        {people.map((p) => (
          <li key={p.person_id}>
            <strong>{p.display_name}</strong> · {p.workspace_email}
            {p.source_ids?.slack ? ` · slack ${p.source_ids.slack}` : ''}
          </li>
        ))}
        {people.length === 0 && <li style={{ color: '#888', listStyle: 'none' }}>Roster is empty.</li>}
      </ul>

      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </section>
  );
}

const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' };
const lbl: React.CSSProperties = { width: 160 };
const fs: React.CSSProperties = { border: '1px solid #ddd', padding: 12 };
const banner: React.CSSProperties = { background: '#fff7e6', border: '1px solid #ffd591', padding: 8, borderRadius: 4 };
